// =====================================================================================
// INVRT Radar — web console — contacts: queries, mutations and small shared helpers
//
// The one read model over identified visitors and revealed people (docs/API-CONTRACTS-C1.md
// §5, api/routes/contacts.ts). Everything here goes through the ApiClient inside TanStack
// Query; nothing computes a credit figure, and nothing assembles a CSV in the browser: the
// export is the server's own file, asked for with the same filters as the list.
//
// Query keys all start with ["contacts", orgId] so an org switch refetches, and so the
// bridge reveal (which writes new lead_results) can invalidate the whole family at once.
// =====================================================================================

import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { downloadTextFile } from "@/lib/csv";
import type {
  Contact,
  ContactSourceFilter,
  ContactsPage,
  DeliveryTargetList,
  SendContactsRequest,
  SendContactsResponse,
} from "@/lib/types.c1";

/** The server's page size ceiling is 200; 50 keeps "Load more" responsive. */
export const CONTACTS_PAGE_LIMIT = 50;
/** POST /contacts/send accepts at most this many ids in one request. */
export const MAX_SEND_IDS = 200;
/** …and at most this many email recipients. */
export const MAX_SEND_RECIPIENTS = 10;
/** The CSV export's hard ceiling, stated next to the button rather than discovered later. */
export const MAX_CSV_ROWS = 5000;

/** The list's filters as the page holds them (dates are plain YYYY-MM-DD from the inputs). */
export interface ContactsFilters {
  source: ContactSourceFilter;
  q: string;
  siteId: string;
  since: string;
  until: string;
}

export const SOURCE_FILTERS: ContactSourceFilter[] = ["all", "identified", "revealed"];

export function isSourceFilter(value: string | null): value is ContactSourceFilter {
  return value !== null && (SOURCE_FILTERS as string[]).includes(value);
}

/** Midnight UTC at the start of a YYYY-MM-DD day, or undefined for anything unparseable. */
function dayStartIso(date: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  const parsed = new Date(`${date}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * The loader's window is [since, until) — `until` is exclusive (_shared/contacts/load.ts
 * applyWindow). A "To" date therefore maps to the start of the FOLLOWING day, so the whole
 * of the chosen day is inside the window rather than none of it.
 */
function nextDayStartIso(date: string): string | undefined {
  const start = dayStartIso(date);
  if (!start) return undefined;
  const parsed = new Date(start);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString();
}

export type ContactsQueryParams = Record<string, string | number | undefined>;

/** The GET /contacts query string for a set of filters, exactly as routes/contacts.ts reads it. */
export function toContactsQuery(filters: ContactsFilters): ContactsQueryParams {
  const q = filters.q.trim();
  return {
    source: filters.source,
    ...(q === "" ? {} : { q }),
    ...(filters.siteId === "" ? {} : { site_id: filters.siteId }),
    ...(filters.since === "" ? {} : { since: dayStartIso(filters.since) }),
    ...(filters.until === "" ? {} : { until: nextDayStartIso(filters.until) }),
  };
}

/** Holds a value back until it has been still for `delayMs`. */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

/** Mirrors the server's gate for POST /contacts/send (requireDeliveryWrite: org admin). The server enforces; this only shows or hides. */
export function useIsOrgAdmin(): boolean {
  const { activeMembership } = useSession();
  return activeMembership?.is_org_admin === true;
}

// ---- reads ------------------------------------------------------------------------------

export function useContactsList(
  filters: ContactsFilters,
): UseInfiniteQueryResult<InfiniteData<ContactsPage, string | null>> {
  const api = useApiClient();
  const { orgId } = useSession();
  const query = toContactsQuery(filters);

  return useInfiniteQuery({
    queryKey: ["contacts", orgId, "list", query],
    queryFn: ({ pageParam }) =>
      api.get<ContactsPage>("/contacts", {
        query: {
          ...query,
          limit: CONTACTS_PAGE_LIMIT,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: null as string | null,
    // next_cursor is null at the end (contract §0), which TanStack reads as "no next page".
    getNextPageParam: (last) => last.next_cursor,
    enabled: Boolean(orgId),
  });
}

/**
 * O1: how deep the ranked panel reads. The server answers the TRUE GLOBAL ranking over the
 * whole organisation's identified contacts (visit_count desc, then last_activity_at desc,
 * a deterministic tiebreak; withheld visits never count), bounded top N with a hard
 * ceiling of 50. This asks for the top 10 of that ranking.
 */
export const TOP_LEADS_LIMIT = 10;

/**
 * What the ranked answer says about itself (routes/contacts.ts O1). "global": the order is
 * the whole organisation's top N and a surface may title it "Top leads". "recent_page": the
 * ranking RPC was unavailable (migration 20261011090000 not yet applied) and only the
 * fetched recent page was ordered - never presented as a global top. Absent on an older
 * API: the same honest recent-page treatment applies.
 */
export type TopLeadsRanking = "global" | "recent_page";

export interface TopLeadsPage extends ContactsPage {
  ranking?: TopLeadsRanking;
}

/**
 * GET /contacts?sort=engagement - the server's own engagement ranking (story C6, global
 * since O1): visit_count desc, then last_activity_at desc, with a deterministic tie-break
 * made server-side. The console ranks NOWHERE else and NEVER re-ranks in the browser
 * (RULED: engagement facts only, server-side); this hook hands back the server's order
 * as-is, plus the ranking marker the copy must honour.
 */
export function useTopLeads(limit: number = TOP_LEADS_LIMIT): UseQueryResult<TopLeadsPage> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["contacts", orgId, "top-leads", limit],
    queryFn: () =>
      api.get<TopLeadsPage>("/contacts", { query: { sort: "engagement", limit } }),
    enabled: Boolean(orgId),
  });
}

/** GET /contacts/:id — the full record (pages and campaign for a visitor). */
export function useContact(
  contactId: string | null,
  placeholder?: Contact,
): UseQueryResult<Contact> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["contacts", orgId, "detail", contactId],
    queryFn: () => api.get<Contact>(`/contacts/${contactId}`),
    enabled: Boolean(orgId) && contactId !== null,
    // The list row, if the caller has one, so the drawer opens on something real while
    // the detail read (pages, campaign) is in flight. Never shown as final: isPlaceholderData
    // stays true until the server's own record lands.
    ...(placeholder ? { placeholderData: placeholder } : {}),
  });
}

/** GET /delivery/targets — every target; callers filter to the active ones. */
export function useDeliveryTargets(enabled = true): UseQueryResult<DeliveryTargetList> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["delivery-targets", orgId],
    queryFn: () => api.get<DeliveryTargetList>("/delivery/targets"),
    enabled: Boolean(orgId) && enabled,
  });
}

// ---- writes -----------------------------------------------------------------------------

/** POST /contacts/send — zero-rated on the server; the body is the contract's own shape. */
export function useSendContacts(): UseMutationResult<
  SendContactsResponse,
  unknown,
  SendContactsRequest
> {
  const api = useApiClient();

  return useMutation({
    mutationFn: (body: SendContactsRequest) =>
      api.post<SendContactsResponse>("/contacts/send", body),
  });
}

/** The server's own filename for the export (_shared/contacts/mail.ts contactsCsvFilename). */
export function contactsCsvFilename(now: Date = new Date()): string {
  return `radar-contacts-${now.toISOString().slice(0, 10)}.csv`;
}

/**
 * GET /contacts?format=csv — the server assembles the file (≤ 5,000 rows, same filters) and
 * the browser merely saves it. The ApiClient hands back the body alone, so the X-Truncated
 * and Content-Disposition headers the route sets are not readable here; the cap is stated
 * beside the button instead, and the filename mirrors the server's own pattern.
 */
export function useContactsCsvDownload(): UseMutationResult<string, unknown, ContactsQueryParams> {
  const api = useApiClient();

  return useMutation({
    mutationFn: async (query: ContactsQueryParams) => {
      const text = await api.get<string>("/contacts", {
        query: { format: "csv", ...query },
        responseType: "text",
      });
      downloadTextFile(contactsCsvFilename(), text);
      return text;
    },
  });
}

// ---- input helpers ----------------------------------------------------------------------

/** Splits a pasted recipient list on commas, semicolons, whitespace and new lines; de-duplicated, order kept. */
export function parseRecipients(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,;]+/)) {
    const trimmed = part.trim();
    if (trimmed === "" || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

// ---- display helpers --------------------------------------------------------------------

/**
 * O8: the one definition of the display-name fallbacks lives in lib/contactDisplay.ts
 * (shared with the ContactCard); re-exported here so the contacts module's existing
 * import seam (the table, the dossier page, the top-leads panel) is unchanged.
 */
export { contactDisplayName } from "@/lib/contactDisplay";

/** "Manchester, GB" / "GB" / null — whichever parts the record carries. */
export function formatLocation(location: {
  city: string | null;
  region: string | null;
  country: string | null;
}): string | null {
  const parts = [location.city, location.region, location.country]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "");
  return parts.length === 0 ? null : parts.join(", ");
}

/** The badge copy for a contact's source. */
export function sourceLabel(source: Contact["source"]): string {
  return source === "identified" ? "Identified" : "Revealed";
}
